import { HttpTypes } from "@medusajs/types"

/**
 * Availability status types
 */
export type AvailabilityStatus = "in-stock" | "low-stock" | "out-of-stock"

/**
 * Availability badge configuration
 */
export interface AvailabilityBadge {
  status: AvailabilityStatus
  label: string
  className: string
}

type StockEnriched = HttpTypes.StoreProduct & { in_stock_now?: boolean; available_units?: number }

/** Copies shown as "low stock" from this number down (inclusive). */
export const LOW_STOCK_THRESHOLD = 3

/**
 * Get product availability status.
 *
 * ONE truth: `in_stock_now` / `available_units` set by the shop backend on
 * every /store/products response (list AND detail) — the same rule the
 * add-to-cart refusal runs. When the backend answered, that answer is
 * final (no second opinion from variant.inventory_quantity). The Medusa
 * heuristic below survives only for responses that carry no flag.
 */
export function getAvailabilityStatus(product: StockEnriched): AvailabilityStatus {
  if (typeof product.in_stock_now === "boolean") {
    if (!product.in_stock_now) return "out-of-stock"
    const n = product.available_units
    if (typeof n === "number" && n <= LOW_STOCK_THRESHOLD) return "low-stock"
    return "in-stock"
  }

  // ── Legacy fallback (no enrichment) ──
  if (!product.variants || product.variants.length === 0) {
    return "out-of-stock"
  }

  const hasInventoryManagement = product.variants.some((variant) => variant.manage_inventory)
  if (!hasInventoryManagement) {
    return "in-stock"
  }

  const totalInventory = product.variants.reduce((total, variant) => {
    if (!variant.manage_inventory) return total
    return total + (variant.inventory_quantity || 0)
  }, 0)

  if (totalInventory === 0) return "out-of-stock"
  if (totalInventory <= LOW_STOCK_THRESHOLD) return "low-stock"
  return "in-stock"
}

/**
 * Get availability badge with label and styling
 * @param product - Product to get badge for
 * @returns Availability badge configuration
 */
export function getAvailabilityBadge(product: StockEnriched): AvailabilityBadge {
  const status = getAvailabilityStatus(product)

  const badges: Record<AvailabilityStatus, AvailabilityBadge> = {
    "in-stock": {
      status: "in-stock",
      label: "In Stock",
      className:
        "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800",
    },
    "low-stock": {
      status: "low-stock",
      label: "Low Stock",
      className:
        "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800",
    },
    "out-of-stock": {
      status: "out-of-stock",
      label: "Out of Stock",
      className:
        "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
    },
  }

  return badges[status]
}
